'use client'

import { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import { useChat } from '@/contexts/ChatContext'
import { useRouter, usePathname } from 'next/navigation'
import { useCurrentWorkspace } from '@/hooks/useCurrentWorkspace'
import { ChatInterface } from '@/components/chat/ChatInterface'
import { cn } from '@/lib/utils'
import { createClient } from '@/utils/supabase/client'
import { emitFunnelEvent } from '@/lib/funnel-events'
import { CHAT_PANEL_WIDTH } from '@/lib/chat-panel-width'
import { commandFormSnapshotPreamble } from '@/lib/commands/form-snapshot'

export function CampaignChatPanel() {
  const {
    chatOpen,
    flowIntent,
    seedMessage,
    autoSendSeed,
    clearAutoSendSeed,
    openChat,
    closeChat,
    currentThreadId,
    setCurrentThreadId,
    pendingFieldContext,
    setPendingFieldContext,
    pendingFieldContexts,
    setPendingFieldContexts,
    pendingEventContext,
    setPendingEventContext,
    pendingCommandFormContext,
    setProposedCommandFormPatches,
    flagUrlChangeSource,
    isOnboarding,
    setCompletingOnboarding,
  } = useChat()
  const router = useRouter()
  // usePathname() can briefly be null/undefined during hydration (Next 15 + parallel routes).
  const pathname = usePathname() ?? ''
  const { workspace, isLoading: workspaceLoading } = useCurrentWorkspace()
  const openPanelRef = useRef<HTMLDivElement | null>(null)
  const onboardingStartedRef = useRef(false)
  const isOnChatRoute = pathname === '/chat'
  const effectiveFlowIntent = isOnChatRoute && isOnboarding ? 'onboarding' : flowIntent

  useLayoutEffect(() => {
    const panel = openPanelRef.current
    if (!panel) return
    if (chatOpen) {
      panel.removeAttribute('inert')
      return
    }
    panel.setAttribute('inert', '')
  }, [chatOpen])

  const endpointBody = useMemo(
    () => ({
      flowIntent: effectiveFlowIntent,
      eventContext: pendingEventContext || undefined,
      commandFormContext: pendingCommandFormContext
        ? {
            commandId: pendingCommandFormContext.commandId,
            pathname: pendingCommandFormContext.pathname,
            formState: pendingCommandFormContext.formState,
            fullFormState: pendingCommandFormContext.fullFormState,
            focusedField: pendingCommandFormContext.focusedField,
          }
        : undefined,
      pathname,
      workspaceId: workspace?.id,
    }),
    [effectiveFlowIntent, pendingEventContext, pendingCommandFormContext, pathname, workspace?.id]
  )

  const apiMessageTransform = useCallback((userMessage: string): string => {
    const commandPreamble = pendingCommandFormContext
      ? commandFormSnapshotPreamble({
          pathname: pendingCommandFormContext.pathname,
          snapshot: pendingCommandFormContext.formState,
          focusedField: pendingCommandFormContext.focusedField,
        })
      : null
    const activeContexts = pendingFieldContexts.length > 0
      ? pendingFieldContexts
      : pendingFieldContext ? [pendingFieldContext] : []
    if (!activeContexts.length && !commandPreamble) return userMessage
    const contextLines = activeContexts.map(ctx => {
      const fieldLabel = ctx.field.replace(/_/g, ' ')
      return ctx.currentValue
        ? `• "${fieldLabel}": "${ctx.currentValue}"`
        : `• "${fieldLabel}"`
    })
    setPendingFieldContext(null)
    setPendingFieldContexts([])
    const fieldPreamble = contextLines.length > 0
      ? `[Context: Discussing the following brand fields]\n${contextLines.join('\n')}`
      : null
    return [commandPreamble, fieldPreamble, userMessage].filter(Boolean).join('\n\n')
  }, [pendingCommandFormContext, pendingFieldContexts, pendingFieldContext, setPendingFieldContext, setPendingFieldContexts])

  const handleSlashCommand = useCallback((intent: string) => {
    openChat(intent as 'campaign' | 'video' | 'brand')
  }, [openChat])

  const handleNavigate = useCallback(
    (url: string) => {
      setPendingEventContext(null)
      flagUrlChangeSource('tool-navigation')
      if (url === '/wallet' || url.startsWith('/wallet?')) {
        router.push(url.replace('/wallet', '/budget'))
        return
      }
      if (url === '/activity') {
        router.push('/activity')
        return
      }
      router.push(url)
    },
    [flagUrlChangeSource, router, setPendingEventContext]
  )

  const handleSkipToDashboard = useCallback(async () => {
    const supabase = createClient()
    const { ensureGuestLobbySession } = await import('@/lib/auth/ensure-guest-lobby-client')
    ensureGuestLobbySession().catch(() => {})
    await supabase.auth.updateUser({ data: { has_onboarded: true } })
    router.push('/campaigns')
  }, [router])

  const handleLogout = useCallback(async () => {
    const supabase = createClient()
    await supabase.auth.signOut()
    router.push('/')
  }, [router])

  const headerActions = useMemo(() => {
    if (!isOnChatRoute) return undefined

    const actions: Array<{ label: string; onClick?: () => void; href?: string }> = []
    if (isOnboarding) {
      actions.push({ label: 'Skip to dashboard', onClick: handleSkipToDashboard })
    }
    actions.push({ label: 'Terms', href: '/terms' })
    actions.push({ label: 'Log out', onClick: handleLogout })
    return actions
  }, [handleLogout, handleSkipToDashboard, isOnChatRoute, isOnboarding])

  const handleUiEvent = useCallback(async (event: Record<string, unknown>) => {
    if (event.type === 'onboarding_complete') {
      emitFunnelEvent('onboarding_completed', 'onboarding')
      const redirectTo = (event.redirect_to as string | undefined) ?? '/campaigns'
      setCompletingOnboarding(true)
      const supabase = createClient()
      await supabase.auth.refreshSession()
      window.setTimeout(() => {
        setCompletingOnboarding(false)
        router.push(redirectTo)
      }, 1800)
      return
    }

    if (event.type === 'workspace_created' || event.type === 'workspace_joined') {
      router.refresh()
    }

    if (event.type === 'tool_result' && event.name === 'propose_command_form_patch' && event.data && typeof event.data === 'object') {
      const data = event.data as Record<string, unknown>
      const patch = data.patch && typeof data.patch === 'object' && !Array.isArray(data.patch)
        ? data.patch as Record<string, unknown>
        : {}
      setProposedCommandFormPatches((prev) => [
        ...prev,
        {
          id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          patch,
          warnings: Array.isArray(data.warnings) ? data.warnings.map(String) : [],
          errors: Array.isArray(data.errors) ? data.errors.map(String) : undefined,
          dangerousToolsAdded: Array.isArray(data.dangerousToolsAdded) ? data.dangerousToolsAdded.map(String) : undefined,
          summary: typeof data.summary === 'string' ? data.summary : 'Command form changes proposed',
          reasoning: typeof data.reasoning === 'string' ? data.reasoning : '',
          createdAt: Date.now(),
        },
      ])
    }

    if (event.type === 'tool_result' && event.name === 'propose_command_graph' && event.data && typeof event.data === 'object') {
      const data = event.data as Record<string, unknown>
      if (data.graph && typeof data.graph === 'object') {
        setProposedCommandFormPatches((prev) => [
          ...prev,
          {
            id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            patch: { deterministic_graph: data.graph as Record<string, unknown>, execution_mode: 'deterministic' },
            warnings: [],
            errors: Array.isArray(data.errors) ? data.errors.map(String) : undefined,
            dangerousToolsAdded: undefined,
            summary: 'Add workflow graph',
            reasoning: typeof data.reasoning === 'string' ? data.reasoning : '',
            createdAt: Date.now(),
          },
        ])
      }
    }
  }, [router, setCompletingOnboarding, setProposedCommandFormPatches])

  const handleThreadCreated = useCallback((id: string) => {
    setCurrentThreadId(id)
    if (effectiveFlowIntent === 'onboarding' && !onboardingStartedRef.current) {
      onboardingStartedRef.current = true
      emitFunnelEvent('onboarding_started', 'onboarding', { source: seedMessage ? 'start_page' : 'direct' })
    }
  }, [effectiveFlowIntent, seedMessage, setCurrentThreadId])

  const onboardingKickoffEndpoint = isOnChatRoute && isOnboarding && currentThreadId && !seedMessage
    ? `/api/chat/threads/${currentThreadId}/kickoff`
    : null

  const fieldContextBanner =
    (pendingFieldContexts.length > 0 || pendingFieldContext) ? (
      <div className="px-4 pt-2 shrink-0">
        <div className="flex flex-col gap-1 rounded-lg bg-atrium-content px-3 py-2 text-xs text-atrium-black">
          <div className="flex items-center justify-between">
            <span className="font-medium text-[#939294]">
              Quoting {(pendingFieldContexts.length || 1)} field{(pendingFieldContexts.length > 1) ? 's' : ''}
            </span>
            <button
              onClick={() => { setPendingFieldContext(null); setPendingFieldContexts([]) }}
              className="text-[#939294] hover:text-atrium-black"
            >
              &times;
            </button>
          </div>
          {(pendingFieldContexts.length > 0 ? pendingFieldContexts : pendingFieldContext ? [pendingFieldContext] : []).map((ctx, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className="font-medium capitalize">{ctx.field.replace(/_/g, ' ')}:</span>
              {ctx.currentValue && (
                <span className="truncate text-[#939294]">
                  {ctx.currentValue.length > 50 ? ctx.currentValue.slice(0, 50) + '...' : ctx.currentValue}
                </span>
              )}
              <button
                onClick={() => {
                  const updated = (pendingFieldContexts.length > 0 ? pendingFieldContexts : [pendingFieldContext!]).filter((_, idx) => idx !== i)
                  setPendingFieldContexts(updated)
                  setPendingFieldContext(updated[updated.length - 1] ?? null)
                }}
                className="ml-auto shrink-0 text-[#939294] hover:text-atrium-black"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      </div>
    ) : null

  return (
    <aside
      data-chat-sidebar
      className={cn(
        'relative flex h-full shrink-0 flex-col overflow-hidden bg-atrium-sidebar transition-[width] duration-200 ease-out motion-reduce:transition-none',
        chatOpen ? 'border-l border-atrium-border' : 'border-l-0',
      )}
      style={{ width: chatOpen ? CHAT_PANEL_WIDTH : 0 }}
    >
      <div
        ref={openPanelRef}
        className={cn(
          'flex min-h-0 flex-1 flex-col transition-transform duration-200 ease-out motion-reduce:transition-none',
          !chatOpen && 'translate-x-full',
        )}
        style={{ width: CHAT_PANEL_WIDTH }}
      >
        <ChatInterface
          endpoint="/api/campaign-chat"
          endpointBody={endpointBody}
          workspaceId={workspace?.id}
          workspaceReady={!workspaceLoading}
          flowIntent={effectiveFlowIntent}
          seedMessage={seedMessage}
          autoSendSeed={autoSendSeed}
          onAutoSendConsumed={clearAutoSendSeed}
          onboardingKickoffEndpoint={onboardingKickoffEndpoint}
          threadId={currentThreadId}
          onThreadCreated={handleThreadCreated}
          onNavigate={handleNavigate}
          onUiEvent={handleUiEvent}
          apiMessageTransform={apiMessageTransform}
          extractEndpoint={currentThreadId ? `/api/chat/threads/${currentThreadId}/extract` : null}
          forceNewThread={!!seedMessage}
          onSlashCommand={handleSlashCommand}
          variant="panel"
          className="min-h-0 flex-1"
          panelActive={chatOpen}
          onPanelCollapse={closeChat}
          panelFrameTop={fieldContextBanner}
          headerActions={headerActions}
        />
      </div>
    </aside>
  )
}
